import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { usersGetLoginNetworkService } from 'services/user';
import { handleApiError } from 'utils';

const NetworkSettingsContext = createContext();

// Default network settings (kept outside the component so its identity is
// stable across renders and safe to reference inside memoized callbacks).
const defaultSettings = {
  hide_logo: false,
  hide_logout: false,
  navbar_color_light_mode: '#444444',
  navbar_color_dark_mode: '#000000',
  navbar_current_mode: 'dark',
  directoryDisplayDevice: 'TOUCH_SCREEN',
  directoryItemsPerSlide: 5,
  directoryAutoplaySpeed: 3000,
};

export const useNetworkSettings = () => {
  const context = useContext(NetworkSettingsContext);
  if (!context) {
    throw new Error(
      'useNetworkSettings must be used within a NetworkSettingsProvider',
    );
  }
  return context;
};

export const NetworkSettingsProvider = ({ children }) => {
  const [networkSettings, setNetworkSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNetworkSettings = useCallback(async () => {
    // Check if we're on login or logout pages and apply default settings
    const currentPath = window.location.pathname;
    console.log('fetchNetworkSettings-currentPath', currentPath);
    if (currentPath === '/login' || currentPath === '/logout') {
      console.log('Login/Logout page detected, applying default settings');
      setNetworkSettings(defaultSettings);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await usersGetLoginNetworkService();
      console.log('response000', response);

      if (
        response.data &&
        response.data.item &&
        response.data.item.branchnetwork
      ) {
        const settings = response.data.item.branchnetwork;
        setNetworkSettings({
          hide_logo: settings.hide_logo ?? defaultSettings.hide_logo,
          hide_logout: settings.hide_logout ?? defaultSettings.hide_logout,
          navbar_color_light_mode:
            settings.navbar_color_light_mode ??
            defaultSettings.navbar_color_light_mode,
          navbar_color_dark_mode:
            settings.navbar_color_dark_mode ??
            defaultSettings.navbar_color_dark_mode,
          navbar_current_mode:
            settings.navbar_current_mode ?? defaultSettings.navbar_current_mode,
          directoryDisplayDevice:
            settings.directoryDisplayDevice ??
            defaultSettings.directoryDisplayDevice,
          directoryItemsPerSlide:
            settings.directoryItemsPerSlide ??
            defaultSettings.directoryItemsPerSlide,
          directoryAutoplaySpeed:
            settings.directoryAutoplaySpeed ??
            defaultSettings.directoryAutoplaySpeed,
        });
      } else {
        // Apply default settings when no network settings are loaded from server
        console.log('No network settings found, applying defaults');
        setNetworkSettings(defaultSettings);
      }
    } catch (err) {
      console.error('Error fetching network settings:', err);
      setError(err);
      handleApiError(err);
      // Apply default settings on error
      setNetworkSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateNetworkSettings = (newSettings) => {
    setNetworkSettings((prev) => ({ ...prev, ...newSettings }));
  };

  useEffect(() => {
    fetchNetworkSettings();
  }, [fetchNetworkSettings]); // Only run once on mount

  const value = {
    networkSettings,
    loading,
    error,
    fetchNetworkSettings,
    updateNetworkSettings,
  };

  return (
    <NetworkSettingsContext.Provider value={value}>
      {children}
    </NetworkSettingsContext.Provider>
  );
};
